import { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { createKeyPayment, getPaymentStatus } from '../../api/payment.api';
import { applyPromocode } from '../../api/promocode.api';
import type { PromocodeResult } from '../../types/promocode';
import { formatPrice } from '../../utils/formatPrice';
import { getApiErrorMessage } from '../../utils/apiError';
import styles from './BuyExtraKeyCard.module.css';

interface BuyExtraKeyCardProps {
  /** Цена дополнительного ключа в копейках (extra_key_price_kopecks плана) */
  priceKopecks: number;
  /** Вызывается после успешной оплаты в stub-режиме (без редиректа) */
  onPurchased?: () => void;
}

export function BuyExtraKeyCard({ priceKopecks, onPurchased }: BuyExtraKeyCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromocodeResult | null>(null);

  const hasPercent = !!appliedPromo && appliedPromo.discountPercent != null;
  const finalPriceKopecks =
    hasPercent && appliedPromo?.discountPercent != null
      ? Math.round((priceKopecks * (100 - appliedPromo.discountPercent)) / 100)
      : priceKopecks;

  const handleClose = () => {
    if (paying) return;
    setIsOpen(false);
    setError('');
    setPromoError('');
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await applyPromocode(code);
      // Промокоды с бонусными днями к докупке ключа не применимы (бекенд ответит 422)
      if (result.discountDays != null && result.discountPercent == null) {
        setAppliedPromo(null);
        setPromoError('Промокод с бонусными днями нельзя применить к покупке ключа');
        return;
      }
      setAppliedPromo(result);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(getApiErrorMessage(err, 'Не удалось применить промокод'));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError('');
    setPromoCode('');
  };

  const handlePay = async () => {
    setPaying(true);
    setError('');
    try {
      const returnUrl = `${window.location.origin}/dashboard/payment/success`;
      const resp = await createKeyPayment(returnUrl, appliedPromo?.code);

      if (resp.confirmationUrl) {
        // Редирект на ЮKassa (или stub-страницу) — как при оплате подписки
        window.location.href = resp.confirmationUrl;
        return;
      }

      // Stub-режим без редиректа: опрашиваем статус платежа
      const paymentId = resp.paymentId;
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const payment = await getPaymentStatus(paymentId);
          if (payment.status === 'succeeded') {
            clearInterval(pollInterval);
            setPaying(false);
            setIsOpen(false);
            toast.success('Дополнительный ключ оплачен и появится в списке', { duration: 6000 });
            onPurchased?.();
          } else if (payment.status === 'cancelled' || attempts > 30) {
            clearInterval(pollInterval);
            setPaying(false);
            setError('Оплата не прошла');
          }
        } catch {
          if (attempts > 30) {
            clearInterval(pollInterval);
            setPaying(false);
            setError('Не удалось проверить статус оплаты');
          }
        }
      }, 2000);
    } catch (err) {
      setPaying(false);
      setError(getApiErrorMessage(err, 'Ошибка при создании платежа. Попробуйте позже'));
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.addCard}
        onClick={() => setIsOpen(true)}
        aria-label="Купить ещё ключ"
      >
        <span className={styles.plus} aria-hidden="true">+</span>
        <span className={styles.addTitle}>Купить ещё ключ</span>
        <span className={styles.addPrice}>{formatPrice(priceKopecks)}</span>
      </button>

      <Modal isOpen={isOpen} onClose={handleClose} title="Дополнительный ключ для устройства">
        <h3 className={styles.modalTitle}>Дополнительный ключ для устройства</h3>

        {hasPercent ? (
          <div className={styles.priceRow}>
            <span className={styles.oldPrice}>{formatPrice(priceKopecks)}</span>
            <span className={styles.price}>{formatPrice(finalPriceKopecks)}</span>
          </div>
        ) : (
          <div className={styles.price}>{formatPrice(priceKopecks)}</div>
        )}

        <p className={styles.hint}>
          Ключ действует, пока активна подписка, и продлевается вместе с ней
        </p>

        <div className={styles.promoSection}>
          {appliedPromo ? (
            <div className={styles.promoApplied}>
              <span className={styles.promoCodeLabel}>{appliedPromo.code}</span>
              {hasPercent && (
                <span className={styles.promoBonus}>Скидка {appliedPromo.discountPercent}%</span>
              )}
              <button type="button" className={styles.promoRemove} onClick={handleRemovePromo}>
                &#10005; убрать
              </button>
            </div>
          ) : (
            <>
              <div className={styles.promoInputRow}>
                <input
                  className={styles.promoInput}
                  type="text"
                  placeholder="Промокод"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyPromo();
                    }
                  }}
                  disabled={promoLoading || paying}
                  aria-label="Промокод"
                />
                <Button
                  variant="secondary"
                  onClick={handleApplyPromo}
                  loading={promoLoading}
                  disabled={!promoCode.trim() || promoLoading || paying}
                >
                  Применить
                </Button>
              </div>
              {promoError && <div className={styles.promoError}>{promoError}</div>}
            </>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <Button fullWidth size="lg" onClick={handlePay} loading={paying}>
          Перейти к оплате {formatPrice(finalPriceKopecks)}
        </Button>
      </Modal>
    </>
  );
}
